import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BTN_DANGER, BTN_QUIET, Confirm, FIELD, useGuard } from "./dialogs";
import PersonSheet, { lastSeen } from "./PersonSheet";
import { FLAG_LABEL, LoadError, Pill, SectionTitle, Skeleton, useLoad, when, type Tone } from "./ui";

// Everyone with an account, then who is linked with whom. Tap a person for everything about
// their account and everything you can do to it.

const STATUS: Record<string, { label: string; tone: Tone }> = {
  active: { label: "Active", tone: "good" },
  slowing: { label: "Slowing down", tone: "watch" },
  quiet: { label: "Gone quiet", tone: "neutral" },
  new: { label: "Hasn't texted yet", tone: "neutral" },
};
const FILTERS: [string, string][] = [
  ["all", "Everyone"], ["attention", "Needs a look"], ["active", "Active"], ["quiet", "Quiet"],
  ["unused", "Looks unused"],
];

function matches(p: any, filter: string): boolean {
  if (filter === "attention") return !!p.needs_attention && !p.looks_unused;
  if (filter === "active") return p.status === "active";
  if (filter === "quiet") return p.status === "quiet" || p.status === "slowing";
  if (filter === "unused") return !!p.looks_unused;
  return true;
}

export default function AdminPeopleSection({ openId, onOpen }: {
  openId: string | null; onOpen: (id: string | null) => void;
}) {
  const guard = useGuard();
  const roster = useLoad<any[]>("/admin/roster");
  const links = useLoad<any[]>("/admin/links");
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [review, setReview] = useState(false);
  const [ending, setEnding] = useState<any>(null);

  const people = (roster.data || []).filter((r) => r.kind === "person");
  const q = query.trim().toLowerCase();
  const digits = q.replace(/\D/g, "");
  const shown = people.filter((p) => matches(p, filter)
    && (!q || (p.name || "").toLowerCase().includes(q)
      || (digits.length >= 3 && (p.phone || "").replace(/\D/g, "").includes(digits))));
  const open = people.find((p) => p.user_id === openId) || null;
  const count = (f: string) => people.filter((p) => matches(p, f)).length;
  const chosen = people.filter((p) => picked.includes(p.user_id));

  // A filter with nobody in it is not worth showing as selected.
  useEffect(() => { if (filter !== "all" && roster.data && count(filter) === 0) setFilter("all"); },
    [roster.data]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: string) => {
    if (!picked.includes(id) && picked.length >= 25) {
      toast.message("25 at a time. Delete these first, then pick more.");
      return;
    }
    setPicked((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
  };
  const stopPicking = () => { setPicking(false); setPicked([]); };

  return (
    <div>
      <SectionTitle kicker="People" title="Everyone with an account" />
      {roster.loading && !roster.data ? <Skeleton rows={4} />
        : roster.error && !roster.data ? <LoadError message={roster.error} onRetry={roster.reload} /> : (
        <div data-testid="admin-people">
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name or number" aria-label="Search name or number"
            className={FIELD} data-testid="people-search" />
          <div className="mt-3 flex flex-wrap gap-2">
            {FILTERS.filter(([key]) => key === "all" || count(key) > 0).map(([key, label]) => (
              <button key={key} type="button" onClick={() => setFilter(key)}
                className={`h-11 rounded-full px-4 text-xs font-bold ${filter === key
                  ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                {label} {count(key)}
              </button>
            ))}
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {picking ? "Tick the accounts to delete. You'll review the list before anything happens."
                : "Tap a person to see their account and change it."}
            </p>
            <button type="button" className="h-11 shrink-0 rounded-full px-3 text-xs font-bold text-primary"
              onClick={() => (picking ? stopPicking() : setPicking(true))} data-testid="people-select">
              {picking ? "Cancel" : "Select"}
            </button>
          </div>

          <div className="mt-1 space-y-2">
            {shown.map((p) => {
              const st = STATUS[p.status] || STATUS.new;
              const locked = p.is_admin || p.state === "deleting";
              const ticked = picked.includes(p.user_id);
              return (
                <button key={p.key} type="button" data-testid={`person-${p.user_id}`}
                  disabled={picking && locked}
                  onClick={() => (picking ? toggle(p.user_id) : onOpen(p.user_id))}
                  className={`flex min-h-[56px] w-full items-center justify-between gap-3 rounded-2xl border bg-card px-4 py-3 text-left disabled:opacity-50 ${ticked ? "border-primary" : "border-border"}`}>
                  {picking && (
                    <span aria-hidden className={`grid h-6 w-6 shrink-0 place-items-center rounded-md border text-sm font-bold ${ticked ? "border-primary bg-primary text-primary-foreground" : "border-input"}`}>
                      {ticked ? "✓" : ""}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold">
                      {p.name}{p.is_admin ? " · admin" : ""}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {lastSeen(p)}{p.phone ? ` · ${p.phone}` : ""}
                    </span>
                  </span>
                  {p.state === "paused" ? <Pill tone="attention">Paused</Pill>
                    : p.state === "deleting" ? <Pill>Being deleted</Pill>
                    : p.texts_off ? <Pill tone="attention">{FLAG_LABEL.texts_off.text}</Pill>
                    : p.looks_unused ? <Pill>Looks unused</Pill>
                    : <Pill tone={st.tone}>{st.label}</Pill>}
                </button>
              );
            })}
            {shown.length === 0 && <p className="text-sm text-muted-foreground">Nobody matches.</p>}
          </div>

          {picking && (
            <div className="sticky bottom-24 z-10 mt-4 flex justify-end">
              <button type="button" className={`${BTN_DANGER} shadow-lg`} disabled={picked.length === 0}
                onClick={() => setReview(true)} data-testid="people-delete-selected">
                Review {picked.length} to delete
              </button>
            </div>
          )}
        </div>
      )}

      <SectionTitle title="Who is linked"
        note="Newest first. If a pair here surprises you, check with them." />
      {links.loading && !links.data ? <Skeleton rows={2} />
        : links.error && !links.data ? <LoadError message={links.error} onRetry={links.reload} /> : (
        <div className="space-y-2" data-testid="admin-links">
          {(links.data || []).map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-3 rounded-2xl bg-secondary/40 px-4 py-2 text-sm">
              <span className="min-w-0">
                <span className="font-bold">{l.a}</span> and <span className="font-bold">{l.b}</span>
                <span className="block text-xs text-muted-foreground">{l.since ? `since ${when(l.since)}` : ""}</span>
              </span>
              <button type="button" className={`${BTN_QUIET} shrink-0`} onClick={() => setEnding(l)}
                data-testid={`end-link-${l.id}`}>End link</button>
            </div>
          ))}
          {(links.data || []).length === 0 && (
            <p className="text-sm text-muted-foreground">Nobody is linked yet.</p>
          )}
        </div>
      )}

      <PersonSheet person={open} onClose={() => onOpen(null)} onChanged={() => { roster.reload(); links.reload(); }} />

      <Confirm open={!!ending} onClose={() => setEnding(null)} action="End link" danger typed="END"
        title={ending ? `End the link between ${ending.a} and ${ending.b}?` : ""}
        body="They will no longer be linked. Lists they shared become two separate copies. Neither of them is told."
        run={async () => {
          await guard(() => api.post(`/admin/links/${ending.id}/end`));
          toast.success("Link ended");
          links.reload(); roster.reload();
        }} />

      <Confirm open={review} onClose={() => setReview(false)} danger
        title={`Delete ${chosen.length} account${chosen.length === 1 ? "" : "s"}?`}
        action="Delete forever" typed={`DELETE ${chosen.length}`}
        body="Everything Remy holds for each of these is permanently deleted, and their invitations are removed. It cannot be undone. Only unused sign-ups can be deleted this way: a real account is skipped, and you delete it from its own page."
        run={async () => {
          const r: any = await guard(() => api.post("/admin/cleanup", {
            user_ids: chosen.map((p) => p.user_id), invite_ids: [], confirm: `DELETE ${chosen.length}`,
          }));
          const skipped = (r.data?.results || []).filter((x: any) => !x.ok);
          if (skipped.length) {
            toast.message(`${r.data.done} deleted, ${skipped.length} skipped. ${skipped[0].why}`);
          } else {
            toast.success(`${r.data.done} deleted. They will be gone in a few minutes.`);
          }
          stopPicking();
          roster.reload();
        }}>
        <div className="max-h-48 overflow-y-auto rounded-2xl border border-border bg-card" data-testid="cleanup-review">
          {chosen.map((p, i) => (
            <div key={p.key} className={`px-4 py-2 text-sm ${i ? "border-t border-border" : ""}`}>
              <span className="font-bold">{p.name}</span>
              <span className="block text-xs text-muted-foreground">
                {[p.phone || "no phone number", lastSeen(p)].join(" · ")}
              </span>
            </div>
          ))}
        </div>
      </Confirm>
    </div>
  );
}
